/**
 * Making a drawn diagram clickable (round 4, owner: "Don't you think those
 * nodes and edges should be clickable in the diagram? Like show some stuff
 * when they are clicked? Maybe message schemas?").
 *
 * Mermaid hands back one string of SVG. There is no handle on the shapes it
 * drew, no callback, no id we minted; the only thing the server and the
 * drawing agree on is the LABEL, because the server wrote it into the mermaid
 * source and mermaid printed it. So the join is textual: find the element
 * whose rendered text IS the label, and turn that element into a button.
 *
 * The search runs in two passes, because mermaid's markup varies by diagram
 * kind and by version:
 *  1. the shapes it names: `g.node`, `g.edgeLabel`, clusters, sequence actors,
 *     state nodes. These are the elements we actually want to light up.
 *  2. any element whose ENTIRE text is that label. Document order puts an
 *     ancestor before its descendants, so the first such element is the
 *     outermost one, which for a node is the label's own group rather than the
 *     bare `<text>` inside it. Containers that hold every node (`g.nodes`,
 *     `g.edgeLabels`, the `<svg>` itself) are skipped by name, or a one-node
 *     diagram would turn the whole drawing into a single button.
 *
 * When neither pass finds it, the part is simply not on the picture: the
 * element stays inert, `unmatched` names the part, and the pane offers those
 * parts as a plain row of buttons instead. Nothing the server sent becomes
 * unreachable because a renderer changed its class names.
 *
 * Decoration is DOM-level and fully reversible: the cleanup this returns
 * removes every attribute, class and listener it added, so a re-render (theme
 * change, new diagram) never leaves a stale hotspot behind.
 */

import type { DiagramPart } from '../chat/transcript';

/** Shapes mermaid names, in the order we would rather match them. */
const PREFERRED_SELECTOR = [
  'g.node',
  'g.edgeLabel',
  'g.cluster',
  'g.actor',
  'g.statediagram-state',
  'g.entityLabel',
  'g.classGroup',
  'text.messageText',
  'text.actor',
].join(', ');

/** Text carriers to start the fallback climb from. */
const TEXT_SELECTOR = 'text, tspan, span, p, div, foreignObject, g';

/**
 * Containers that hold EVERY node or edge: matching one of these would make
 * the whole drawing a single button, which is worse than matching nothing.
 */
const CONTAINER_CLASSES = new Set([
  'nodes',
  'edgeLabels',
  'edgePaths',
  'clusters',
  'root',
  'output',
  'graph',
  'actors',
  'messages',
]);

/** The attribute the panel, the styles and the pan surface all key off. */
export const PART_ATTR = 'data-part-label';

/**
 * Membership of the round-6 selection SET, kept deliberately apart from
 * round 4's `data-part-selected` (the ONE part whose detail panel is open).
 * Two attributes rather than one because the two states mean different things
 * and are written by different passes: MermaidDiagram marks the open part,
 * the pane marks the set, and neither may erase the other's work.
 */
export const SELECTION_ATTR = 'data-part-in-selection';

export interface DecorateOptions {
  /** told the label of the part the reader activated */
  onSelect: (label: string) => void;
}

export interface DecorateResult {
  /** labels that found an element and are now interactive */
  matched: string[];
  /** labels with nowhere to land in this drawing */
  unmatched: DiagramPart[];
  /** undo everything this call did to the DOM */
  cleanup: () => void;
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};

/**
 * Whitespace-folded, case-insensitive, entity-decoded text: what "the same
 * label" means. Mermaid renders `&amp;` as `&`, so a part whose label kept
 * the entity (the model escaped it to satisfy the server's coverage check)
 * must still find its box (audit 2026-09-07).
 */
export function normalizeLabel(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/&(amp|lt|gt|quot|apos|nbsp|#39);/g, (m) => ENTITIES[m] ?? m)
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * The edge PATH an edge label sits on (owner, 2026-09-07: "when an edge is
 * focused, the entire line should be highlighted too; right now it's hard
 * to tell where the line goes"). Mermaid gives the label and its path no
 * shared handle we can rely on across versions, but it places every label
 * on its path's midpoint, so the path whose midpoint is nearest the label
 * is the label's path. Screen coordinates, so pan/zoom transforms cancel.
 */
const edgePathOf = new WeakMap<Element, SVGPathElement>();

function linkEdgePath(labelEl: Element): SVGPathElement | null {
  const known = edgePathOf.get(labelEl);
  if (known !== undefined) return known;
  const svg = labelEl.closest('svg');
  if (svg === null) return null;
  const box = labelEl.getBoundingClientRect();
  if (box.width === 0 && box.height === 0) return null;
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  let best: SVGPathElement | null = null;
  let bestDistance = Infinity;
  for (const path of Array.from(svg.querySelectorAll<SVGPathElement>('.edgePaths path, path.flowchart-link'))) {
    if (typeof path.getTotalLength !== 'function') continue;
    const ctm = path.getScreenCTM();
    if (ctm === null) continue;
    let length = 0;
    try {
      length = path.getTotalLength();
    } catch {
      continue;
    }
    const mid = path.getPointAtLength(length / 2).matrixTransform(ctm);
    const distance = Math.hypot(mid.x - cx, mid.y - cy);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = path;
    }
  }
  // Farther than this and the nearest path is some other edge's: leave the
  // label alone rather than light the wrong line.
  if (best !== null && bestDistance <= Math.max(box.width, box.height) * 3 + 24) {
    edgePathOf.set(labelEl, best);
    return best;
  }
  return null;
}

/** Mirror a state attribute from an edge label onto its path. */
function mirrorOnPath(el: Element, name: string, value: string | null): void {
  if (!el.classList.contains('edgeLabel')) return;
  const path = linkEdgePath(el);
  if (path === null) return;
  if (value === null) path.removeAttribute(name);
  else path.setAttribute(name, value);
}

function isContainer(el: Element): boolean {
  if (el.tagName.toLowerCase() === 'svg') return true;
  for (const cls of Array.from(el.classList)) {
    if (CONTAINER_CLASSES.has(cls)) return true;
  }
  return false;
}

/**
 * The element that IS this label. Preferred shapes first; then the outermost
 * element whose whole text is the label, which for a mermaid node is the
 * label's `<g>` and for a bare `<text>` is the text itself.
 */
export function findPartElement(root: ParentNode, label: string): Element | null {
  const target = normalizeLabel(label);
  if (target.length === 0) return null;

  for (const el of Array.from(root.querySelectorAll(PREFERRED_SELECTOR))) {
    if (!isContainer(el) && normalizeLabel(el.textContent) === target) return el;
  }

  // Fallback: document order puts an ancestor before its descendants, so the
  // FIRST element whose entire text is the label is the outermost one.
  for (const el of Array.from(root.querySelectorAll(TEXT_SELECTOR))) {
    if (!isContainer(el) && normalizeLabel(el.textContent) === target) return el;
  }
  return null;
}

/**
 * Turn every part that can be found into a real button on the drawing, and
 * report the ones that could not be found.
 */
/**
 * How one kind of picture finds and names its parts (DECISIONS #94): the
 * drawing's parts by label text, the runtime map's nodes by mermaid id. The
 * hotspot itself (role, focus, the click and the keys, the undo) is the
 * same for both.
 */
export interface DecorateSpec<P> {
  find: (part: P) => Element | null;
  /** what `onSelect` is told, and what `matched` lists */
  key: (part: P) => string;
  /** the PART_ATTR value, which the selection marks and the marquee read */
  label: (part: P) => string;
  ariaLabel: (part: P) => string;
  extraAttrs?: (part: P) => Record<string, string>;
  onSelect: (key: string) => void;
}

export function decorateParts<P>(
  parts: readonly P[],
  spec: DecorateSpec<P>,
): { matched: string[]; unmatched: P[]; cleanup: () => void } {
  const matched: string[] = [];
  const unmatched: P[] = [];
  const undo: Array<() => void> = [];
  const claimed = new Set<Element>();

  for (const part of parts) {
    const el = spec.find(part);
    if (el === null || claimed.has(el)) {
      unmatched.push(part);
      continue;
    }
    claimed.add(el);
    const key = spec.key(part);
    matched.push(key);

    const activate = () => spec.onSelect(key);
    const onClick = (event: Event) => {
      event.stopPropagation();
      activate();
    };
    const onKeyDown = (event: Event) => {
      const k = (event as KeyboardEvent).key;
      if (k !== 'Enter' && k !== ' ' && k !== 'Spacebar') return;
      // Space scrolls the pane otherwise, and Enter would reach the pane.
      event.preventDefault();
      event.stopPropagation();
      activate();
    };
    const extra = spec.extraAttrs?.(part) ?? {};

    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute(PART_ATTR, spec.label(part));
    el.setAttribute('aria-label', spec.ariaLabel(part));
    for (const [name, value] of Object.entries(extra)) el.setAttribute(name, value);
    el.classList.add('lm-diagram-part');
    el.addEventListener('click', onClick);
    el.addEventListener('keydown', onKeyDown);
    // Hovering an edge's label lights the line it sits on, so the reader can
    // see where it goes before deciding to open it (2026-09-07).
    const onEnter = () => mirrorOnPath(el, 'data-part-hover', 'true');
    const onLeave = () => mirrorOnPath(el, 'data-part-hover', null);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('focus', onEnter);
    el.addEventListener('blur', onLeave);

    undo.push(() => {
      el.removeEventListener('click', onClick);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('focus', onEnter);
      el.removeEventListener('blur', onLeave);
      for (const name of ['data-part-selected', SELECTION_ATTR, 'data-part-hover']) {
        mirrorOnPath(el, name, null);
      }
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
      el.removeAttribute(PART_ATTR);
      el.removeAttribute('aria-label');
      for (const name of Object.keys(extra)) el.removeAttribute(name);
      el.removeAttribute('data-part-selected');
      el.removeAttribute('aria-current');
      el.removeAttribute(SELECTION_ATTR);
      el.removeAttribute('aria-pressed');
      el.removeAttribute('data-part-change');
      el.classList.remove('lm-diagram-part');
    });
  }

  return {
    matched,
    unmatched,
    cleanup: () => {
      for (const step of undo) step();
    },
  };
}

export function decorateDiagramParts(
  root: HTMLElement,
  parts: readonly DiagramPart[],
  { onSelect }: DecorateOptions,
): DecorateResult {
  return decorateParts(parts, {
    find: (part) => findPartElement(root, part.label),
    key: (part) => part.label,
    label: (part) => part.label,
    ariaLabel: (part) => `${part.label}: ${part.what}`,
    onSelect,
  });
}

/**
 * Draw ONE part as the current one. Deliberately separate from decoration:
 * tearing the hotspots down and rebuilding them on every selection would
 * remove `tabindex` from the element that has focus, which blurs it, and the
 * reader who pressed Enter would lose their place.
 */
export function markSelectedPart(root: ParentNode | null, label: string | null): void {
  if (root === null) return;
  for (const el of Array.from(root.querySelectorAll(`[${PART_ATTR}]`))) {
    if (label !== null && el.getAttribute(PART_ATTR) === label) {
      el.setAttribute('data-part-selected', 'true');
      el.setAttribute('aria-current', 'true');
      mirrorOnPath(el, 'data-part-selected', 'true');
    } else {
      el.removeAttribute('data-part-selected');
      el.removeAttribute('aria-current');
      mirrorOnPath(el, 'data-part-selected', null);
    }
  }
}

/**
 * Draw the SET the reader has picked (round 6). Same discipline as
 * `markSelectedPart` and for the same reason: a separate pass over elements
 * that already exist, never a re-decoration. Selection changes on every
 * shift-click, and rebuilding the hotspots on each one would strip `tabindex`
 * off the element under the reader's finger.
 *
 * `aria-pressed` rides along on MEMBERS ONLY. A part is an ordinary button
 * until it is in a selection; announcing "not pressed" on every unpicked box
 * of a twenty-node drawing would bury the two that are.
 */
export function markSelectedParts(root: ParentNode | null, labels: readonly string[]): void {
  if (root === null) return;
  const wanted = new Set(labels);
  for (const el of Array.from(root.querySelectorAll(`[${PART_ATTR}]`))) {
    const label = el.getAttribute(PART_ATTR);
    if (label !== null && wanted.has(label)) {
      el.setAttribute(SELECTION_ATTR, 'true');
      el.setAttribute('aria-pressed', 'true');
      mirrorOnPath(el, SELECTION_ATTR, 'true');
    } else {
      el.removeAttribute(SELECTION_ATTR);
      el.removeAttribute('aria-pressed');
      mirrorOnPath(el, SELECTION_ATTR, null);
    }
  }
}

/** A rectangle in CLIENT (viewport) coordinates. */
export interface ScreenRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Every part the rubber band touches.
 *
 * Hit-tested in CLIENT coordinates against `getBoundingClientRect`, which is
 * the one measurement that already has the pane's pan/zoom transform baked
 * into it: the reader drags over what they can see, and no arithmetic has to
 * agree with the transform for that to keep working at any scale.
 *
 * INTERSECTS, not contains (the owner's word was "select any group of nodes
 * and edges"): a band drawn across a row of boxes takes the boxes it crosses,
 * which is what dragging over them looks like it should do. Elements with no
 * area are skipped, so an empty `<g>` cannot be caught by a band that happens
 * to pass over the origin.
 */
export function partsInRect(root: ParentNode | null, rect: ScreenRect): string[] {
  if (root === null) return [];
  const found: string[] = [];
  for (const el of Array.from(root.querySelectorAll(`[${PART_ATTR}]`))) {
    const label = el.getAttribute(PART_ATTR);
    if (label === null) continue;
    const box = el.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) continue;
    if (box.right < rect.left || box.left > rect.right) continue;
    if (box.bottom < rect.top || box.top > rect.bottom) continue;
    found.push(label);
  }
  return found;
}

/**
 * The label of the part an event landed on, if it landed on one. Mermaid nests
 * the words several elements deep inside the shape, so the target of a click
 * is almost never the hotspot itself.
 */
export function partLabelAt(target: EventTarget | null): string | null {
  const el = target as Element | null;
  if (el === null || typeof el.closest !== 'function') return null;
  return el.closest(`[${PART_ATTR}]`)?.getAttribute(PART_ATTR) ?? null;
}

/**
 * Put keyboard focus back on a part of the drawing, if it is still there.
 * Scanned rather than selected: a label is prose and may hold quotes or
 * brackets, which a selector would have to escape and could still get wrong.
 */
export function focusPart(root: ParentNode | null, label: string): void {
  if (root === null) return;
  for (const el of Array.from(root.querySelectorAll(`[${PART_ATTR}]`))) {
    if (el.getAttribute(PART_ATTR) !== label) continue;
    if (typeof (el as HTMLElement).focus === 'function') (el as HTMLElement).focus();
    return;
  }
}
